/**
 * Alchemy RPC Provider
 * Tertiary provider for failover (HTTP only - Alchemy does not support Solana WebSockets)
 */

import * as Sentry from '@sentry/react';
import { RPCProvider, HealthCheckResult, ProviderId } from '../types';

export class AlchemyProvider implements RPCProvider {
  readonly id: ProviderId = 'alchemy';
  readonly name = 'Alchemy';
  readonly supportsWebhooks = false;

  private httpEndpoint: string;

  constructor(httpEndpoint: string) {
    this.httpEndpoint = httpEndpoint;
  }

  getHttpEndpoint(): string {
    return this.httpEndpoint;
  }

  /**
   * Alchemy does NOT support WebSockets for Solana (only EVM chains)
   * Always returns undefined
   */
  getWsEndpoint(): string | undefined {
    return undefined;
  }

  async healthCheck(): Promise<HealthCheckResult> {
    const startTime = performance.now();

    try {
      // Tag this request for Sentry monitoring
      Sentry.setTag('rpc_provider', 'alchemy');

      const response = await fetch(this.httpEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getSlot',
          params: [],
        }),
      });

      const latency = Math.round(performance.now() - startTime);

      if (!response.ok) {
        return {
          healthy: false,
          latency,
          timestamp: Date.now(),
          error: `HTTP ${response.status}`,
        };
      }

      const data = await response.json();

      // getSlot returns a number for healthy nodes
      const healthy = typeof data.result === 'number';

      return {
        healthy,
        latency,
        timestamp: Date.now(),
        error: healthy ? undefined : 'Invalid response',
      };
    } catch (error) {
      const latency = Math.round(performance.now() - startTime);
      return {
        healthy: false,
        latency,
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
